/**
 * Mid-term pricing by the class's REAL meetings.
 *
 * A family joining a running class pays for the meetings it has left:
 * `remaining / total × term price`. Both counts come from the class's
 * materialized `section_instances`, so a skipped meeting, a make-up class, a
 * moved date and a changed weekday are all already in them.
 *
 * ⚠️ This replaced an estimate built from the TERM's dates — weeks since the
 * term started, ROUNDED to the nearest week. A Friday class in a term starting
 * the weekend before lost its next meeting every Thursday: half the week had
 * gone, so the week rounded up and the meeting that had not happened yet was
 * counted as taught. The course page said "4 of 12 remaining" with five left,
 * and checkout charged for four. It also never saw a skip or a make-up.
 *
 * Pure and client-safe: the pay button shows the price the server will charge
 * for the class the parent picked, and the two must not be able to disagree.
 */
export type ClassMeetingCount = {
  /** Every meeting the class has this term, cancelled ones excluded. */
  total: number;
  /** Of those, the ones that have not started yet. */
  remaining: number;
};

export function prorateByMeetings(
  meetings: ClassMeetingCount,
  termPriceCents: number
): number {
  if (meetings.total <= 0 || meetings.remaining <= 0) return 0;
  if (meetings.remaining >= meetings.total) return termPriceCents;
  return Math.round((meetings.remaining / meetings.total) * termPriceCents);
}
